/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * P-5 (S629) — remembering the customer's language between visits.
 *
 * The ADDRESS is the source of truth for the language; this storage is only the
 * fallback used when the address does not name one, and the memory that lets a
 * customer who chose German once arrive in German next time. That order matches
 * how the Deskshare website behaves, so a customer moving between the two never
 * sees the two disagree.
 *
 * Every read and write is wrapped: three of this shop's pages open inside a
 * desktop product's own window, and storage is not guaranteed there any more
 * than it is in a private browsing window. A shop that throws because it could
 * not remember a language preference would be a worse fault than forgetting it.
 */

import {
  DEFAULT_LANGUAGE,
  isSupportedLang,
  type SupportedLanguage,
} from "@/lib/supported-languages";

export const LANGUAGE_STORAGE_KEY = "deskshare_shop_lang";

/** The stored language, or null when nothing usable is stored. */
export function readStoredLanguage(): SupportedLanguage | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isSupportedLang(stored) ? (stored as SupportedLanguage) : null;
  } catch {
    return null;
  }
}

/** Remember a language. Unsupported values are ignored rather than stored. */
export function storeLanguage(lang: unknown): void {
  if (!isSupportedLang(lang)) return;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang.toLowerCase());
  } catch {
    /* storage unavailable — the address still carries the language */
  }
}

/** The stored language, or English. Never throws and never returns a stray code. */
export function preferredLanguage(): SupportedLanguage {
  return readStoredLanguage() ?? DEFAULT_LANGUAGE;
}
